import React from "react";
import confetti from "canvas-confetti";

// Shared frame and field pieces for the step-by-step onboarding flows (T10):
// Add LMBP and Add Franchise.

export const ORANGE = "#FF702B";

export const GSTIN_FORMAT = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
export const DOC_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/jpg", "image/gif"];
export const MAX_DOC_KB = 500;

export const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: "16px 20px",
};

export const inputClass = (error, readOnly) =>
  `w-full h-[42px] rounded-lg border px-3 text-sm focus:outline-none focus:border-[#FF702B] ${
    error ? "border-red-500" : "border-gray-300"
  } ${readOnly ? "bg-gray-100" : "bg-white"}`;

export const addressError = (value, n, required) => {
  const v = (value || "").trim();
  if (!v) return required ? "Address is required" : null;
  if (v.length < 5 || v.length > 150) return `Address ${n} must be between 5 and 150 characters`;
  if (!/^[A-Za-z0-9,./#-][A-Za-z0-9,./# -]*[A-Za-z0-9,./#-]$/.test(v))
    return `Address ${n} contains invalid characters or starts/ends with spaces`;
  if (!/[A-Za-z]/.test(v)) return `Address ${n} must contain at least one letter`;
  return null;
};

export const celebrate = () => {
  try {
    confetti({ particleCount: 250, spread: 100, origin: { y: 0.6 }, zIndex: 3000 });
  } catch (e) {}
};

export const Field = ({ label, required, error, hint, children, full }) => (
  <div style={{ gridColumn: full ? "1 / -1" : undefined, minWidth: 0 }}>
    <label className="text-sm font-medium text-gray-700 mb-1 block">
      {label}
      {required && <span style={{ color: "#dc2626" }}> *</span>}
    </label>
    {children}
    {error ? (
      <div className="error" style={{ color: "#dc2626", fontSize: "12px", marginTop: "4px" }}>
        {error}
      </div>
    ) : hint ? (
      <div style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px" }}>{hint}</div>
    ) : null}
  </div>
);

export const Choice = ({ selected, onClick, title, text, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={selected}
    style={{
      textAlign: "left",
      border: `2px solid ${selected ? ORANGE : "#e5e7eb"}`,
      background: selected ? "#fff4ee" : "#fff",
      borderRadius: "12px",
      padding: "12px 14px",
      flex: "1 1 200px",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
    }}
  >
    <div style={{ fontWeight: 600 }}>{title}</div>
    <div style={{ fontSize: "12px", color: "#6b7280" }}>{text}</div>
  </button>
);

// One upload tile: label, chosen file (with Remove) or a "Choose file" button
export const DocumentTile = ({ label, required, file, error, onFile, onRemove, missing }) => (
  <div
    style={{
      border: `1px dashed ${file ? ORANGE : missing ? "#dc2626" : "#d1d5db"}`,
      borderRadius: "12px",
      padding: "12px",
      background: file ? "#fff8f4" : "#fafafa",
      minWidth: 0,
    }}
  >
    <div style={{ fontWeight: 600, fontSize: "14px" }}>
      {label}
      {required && <span style={{ color: "#dc2626" }}> *</span>}
    </div>
    {file ? (
      <div style={{ fontSize: "12px", color: "#374151", marginTop: "6px", overflowWrap: "anywhere" }}>
        {file.name} · {(file.size / 1024).toFixed(1)} KB
        <div>
          <button type="button" className="btn btn-link p-0" style={{ fontSize: "12px", color: "#dc2626" }} onClick={onRemove}>
            Remove
          </button>
        </div>
      </div>
    ) : (
      <label className="btn btn-sm btn-outline-secondary" style={{ marginTop: "8px" }}>
        Choose file
        <input
          type="file"
          accept="image/*,.pdf"
          hidden
          onChange={(e) => {
            onFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </label>
    )}
    {(error || (missing && !file)) && (
      <div style={{ color: "#dc2626", fontSize: "12px", marginTop: "4px" }}>{error || `${label} is required.`}</div>
    )}
  </div>
);

// Checks a picked file against the allowed types and size; returns an error message or null
export const fileError = (file) => {
  if (!DOC_TYPES.includes(file.type)) return "Only image and PDF files are allowed.";
  if (file.size / 1024 > MAX_DOC_KB) return `File size should not exceed ${MAX_DOC_KB}KB.`;
  return null;
};

// Review step: one card per earlier step, each with an Edit link back to it
export const ReviewSections = ({ sections, onEdit }) => (
  <div style={{ display: "grid", gap: "12px" }}>
    {sections.map((s) => (
      <div key={s.title} style={{ border: "1px solid #e5e7eb", borderRadius: "12px", padding: "12px 16px" }}>
        <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: "6px" }}>
          <div style={{ fontWeight: 600 }}>{s.title}</div>
          <button type="button" className="btn btn-link p-0" style={{ color: ORANGE, fontSize: "13px" }} onClick={() => onEdit(s.step)}>
            Edit
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(110px, 160px) 1fr", gap: "4px 12px", fontSize: "14px" }}>
          {s.rows.map(([k, v]) => (
            <React.Fragment key={k}>
              <div style={{ color: "#6b7280" }}>{k}</div>
              <div style={{ overflowWrap: "anywhere" }}>{v || "—"}</div>
            </React.Fragment>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// Dialog with the step list on the left, the current step on the right and Back / Continue below
export const WizardShell = ({
  className,
  title,
  steps,
  step,
  onStepClick,
  onClose,
  onBack,
  onNext,
  onSubmit,
  nextDisabled,
  submitting,
  submitLabel,
  submittingLabel,
  children,
}) => {
  const isLast = step === steps.length - 1;
  return (
    <div
      className={className}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1055, // below SweetAlert (1060) so alerts show on top
        background: "rgba(15,23,42,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        className="bg-white"
        style={{
          width: "min(1040px, 100%)",
          height: "min(720px, 100%)",
          borderRadius: "18px",
          display: "flex",
          overflow: "hidden",
          boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
        }}
      >
        {/* Step list */}
        <aside
          className="d-none d-md-flex flex-column"
          style={{ width: "250px", background: "#fff7f2", padding: "24px 18px", borderRight: "1px solid #fde4d6", flexShrink: 0 }}
        >
          <div style={{ fontWeight: 700, fontSize: "18px", marginBottom: "18px" }}>{title}</div>
          <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {steps.map((s, i) => {
              const done = i < step;
              const active = i === step;
              return (
                <li key={s.key}>
                  <button
                    type="button"
                    onClick={() => onStepClick(i)}
                    aria-current={active ? "step" : undefined}
                    style={{ display: "flex", gap: "12px", alignItems: "flex-start", background: "none", border: "none", padding: "8px 0", textAlign: "left", width: "100%" }}
                  >
                    <span
                      style={{
                        width: "28px",
                        height: "28px",
                        borderRadius: "50%",
                        flexShrink: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "13px",
                        fontWeight: 700,
                        background: active || done ? ORANGE : "#fff",
                        color: active || done ? "#fff" : "#9ca3af",
                        border: `2px solid ${active || done ? ORANGE : "#e5e7eb"}`,
                      }}
                    >
                      {done ? "✓" : i + 1}
                    </span>
                    <span>
                      <span style={{ display: "block", fontWeight: 600, color: active ? "#111827" : "#4b5563" }}>{s.title}</span>
                      <span style={{ display: "block", fontSize: "12px", color: "#9ca3af" }}>{s.hint}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        {/* Current step */}
        <section className="d-flex flex-column" style={{ flex: 1, minWidth: 0 }}>
          <header className="d-flex justify-content-between align-items-start" style={{ padding: "20px 24px 8px" }}>
            <div>
              <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                Step {step + 1} of {steps.length}
              </div>
              <h5 className="mb-0" style={{ fontWeight: 700 }}>
                {steps[step].title}
              </h5>
            </div>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </header>
          <div className="d-md-none" style={{ height: "4px", background: "#f3f4f6", margin: "0 24px" }}>
            <div style={{ height: "100%", width: `${((step + 1) / steps.length) * 100}%`, background: ORANGE, borderRadius: "4px" }} />
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>{children}</div>

          <footer className="d-flex justify-content-between align-items-center" style={{ padding: "14px 24px", borderTop: "1px solid #f3f4f6", gap: "10px" }}>
            <button type="button" className="btn btn-outline-secondary" onClick={step === 0 ? onClose : onBack}>
              {step === 0 ? "Cancel" : "Back"}
            </button>
            {isLast ? (
              <button type="button" className="btn" style={{ background: ORANGE, color: "#fff", minWidth: "140px" }} disabled={submitting} onClick={onSubmit}>
                {submitting ? submittingLabel : submitLabel}
              </button>
            ) : (
              <button type="button" className="btn" style={{ background: ORANGE, color: "#fff", minWidth: "140px" }} onClick={onNext} disabled={nextDisabled}>
                Continue
              </button>
            )}
          </footer>
        </section>
      </div>
    </div>
  );
};
